/* eslint no-use-before-define: off */

import { DATA_TRACK_TYPE_BANNER, DATA_TRACK_TYPE_CTA } from './constants';

type AdobeLayer = Record<string, unknown>[];
type AnalyticsWindow = {
  location: Pick<Location, 'href'>;
  screen: Pick<Screen, 'height' | 'width'>;
};
type AdobeWindow = AnalyticsWindow & { adobeDataLayer?: AdobeLayer };
type PageOptions = {
  locale?: string; pageCategory?: string; sections?: string[]; title?: string | null;
};
type Collector = { collectorNumber?: string | null; visitInfo?: { tier?: string; balance?: number } };
declare global {
  interface Window { adobeDataLayer?: AdobeLayer }
}

// Complete
const ATM_CONSTANTS = {
  domain: 'earnmore.bluerewards.ca',
  section: 'brs',
  url: 'https://earnmore.bluerewards.ca',
  locale: 'en',
  loginStatus: 'not_logged_in',
  categories: {
    popUp: 'pop-up',
    catalogue: 'catalogue',
    account: 'account',
    page: 'page',
  },
};

// Complete
const initAdobeDataLayer = (targetWindow: AdobeWindow) => {
  // eslint-disable-next-line no-param-reassign
  targetWindow.adobeDataLayer = targetWindow.adobeDataLayer ? targetWindow.adobeDataLayer : [];
};

// Complete
const addAdobeDataEventListeners = () => {
  initAdobeDataLayer(window);
  window.addEventListener('click', onCTAClick);
};

// TODO: Refactor/Rename?
const pushObjToDataLayer = (data: Record<string, unknown>) => {
  window.adobeDataLayer?.push({ ...data });
};

const getCollectorInfo = (user: Collector | null | undefined) => {
  if (user && user.collectorNumber) {
    return {
      id: user.collectorNumber,
      tier: user.visitInfo?.tier,
      login_status: 'logged_in',
      points: user.visitInfo?.balance || 0,
    };
  }

  return {
    id: '',
    tier: '',
    login_status: 'not_logged_in',
    points: 0,
  };
};

// Complete
const getSiteDetails = (window: AnalyticsWindow, locale?: string) => ({
  domain: ATM_CONSTANTS.domain,
  section: ATM_CONSTANTS.section,
  subsection: '',
  language: locale || 'en',
  screen_height: window.screen.height,
  screen_width: window.screen.width,
});

const getPageSectionValue = (processedSections: (string | undefined)[], depth: number) => {
  if (processedSections.length < depth) {
    return '';
  }

  return constructAdobeString([ATM_CONSTANTS.section, ...processedSections.slice(0, depth)]);
};

/**
 * @param {AnalyticsWindow} window
 * @param {{ pageCategory?: string, sections?: string[], title?: string | null }} [pageOptions]
 */
const getPageDetails = (window: AnalyticsWindow, pageOptions: PageOptions = {}) => {
  const {
    pageCategory = ATM_CONSTANTS.categories.catalogue,
    sections = [],
    title = '',
  } = pageOptions;

  const {
    location: {
      href: url = ATM_CONSTANTS.url,
    },
  } = window;

  const processedSections = sections
    .filter(Boolean)
    .map(normalizeString);

  return {
    name: constructAdobeString([
      ATM_CONSTANTS.section,
      ...processedSections,
    ]),
    previousPageName: '',
    title: title || '',
    url,
    section: getPageSectionValue(processedSections, 1),
    subSection1: getPageSectionValue(processedSections, 2),
    subSection2: getPageSectionValue(processedSections, 3),
    subSection3: getPageSectionValue(processedSections, 4),
    category: pageCategory,
  };
};

/**
 * @param {AnalyticsWindow} window
 * @param {object} user
 * @param {{
 *   locale?: string,
 *   pageCategory?: string,
 *   sections?: string[],
 *   title?: string | null
 * }} [pageOptions]
 */
const pushPageViewEvent = (window: AdobeWindow, user: Collector | null | undefined, pageOptions: PageOptions = {}) => {
  initAdobeDataLayer(window);

  const payload = {
    event: 'pageViews',
    web: {
      pageDetails: getPageDetails(window, pageOptions),
      siteDetails: getSiteDetails(window, pageOptions?.locale),
      collector: getCollectorInfo(user),
    },
  };

  pushObjToDataLayer(payload);
};

// Complete ??
const constructAdobeString = (parts: (string | undefined)[] = []) => {
  if (parts && parts.length > 0) {
    return parts.filter((part) => part && part !== '').join(':');
  }

  return '';
};

// TODO: Refactor/Rename 'cat'
const normalizeString = (cat: string) => (
  cat.replaceAll(' ', '_').replaceAll(',', '').toLowerCase()
) || undefined;

// Complete??
const hasClosest = (target: unknown): target is { closest(selector: string): HTMLElement | null } => (
  !!target && typeof target === 'object' && 'closest' in target && typeof target.closest === 'function'
);
const onCTAClick = (e: { target: EventTarget | null }) => {
  const element = hasClosest(e.target) ? e.target.closest('[data-track-type]') : null;

  if (!element) {
    return;
  }

  const { dataset = {} } = element;
  const href = 'href' in element && typeof element.href === 'string' ? element.href : '';

  const {
    trackId,
    trackClick,
    trackType,
    trackLocation = '',
    trackBannerName,
  } = dataset;

  if (!trackId || !trackClick || (trackType === DATA_TRACK_TYPE_BANNER && !trackBannerName)) {
    return;
  }

  const payload: { event?: string; web?: Record<string, unknown> } = {};

  switch (trackType) {
    case DATA_TRACK_TYPE_BANNER:
      payload.event = 'bannerClicks';
      payload.web = {
        banner: {
          linkName: trackId,
          bannerName: trackBannerName,
          bannerCategory: trackClick,
          bannerLocation: trackLocation,
        },
      };
      break;
    case DATA_TRACK_TYPE_CTA:
      payload.event = 'CTAClick';
      payload.web = {
        CTAClick: {
          linkName: trackId,
          linkLocation: trackClick,
          linkhref: href,
        },
      };
      break;
    default:
      payload.event = 'internalLinkClick';
      payload.web = {
        internalLink: {
          linkName: trackId,
          linkLocation: trackClick,
          linkhref: href,
        },
      };
  }

  window.adobeDataLayer?.push(payload);
};

export {
  ATM_CONSTANTS,
  onCTAClick,
  addAdobeDataEventListeners,
  pushPageViewEvent,
};
